import React, { useRef, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Download, QrCode, Loader2, Zap, Anchor, ClipboardCheck } from 'lucide-react';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';

interface InspectionQRModalProps {
    isOpen: boolean;
    onClose: () => void;
    asset: any;
    hasInspectionTemplate: boolean;
}

const InspectionQRModal: React.FC<InspectionQRModalProps> = ({ isOpen, onClose, asset, hasInspectionTemplate }) => {
    const qrRef = useRef<HTMLDivElement>(null);
    const [downloading, setDownloading] = useState(false);
    
    // Determine default tab based on asset type or register flags
    const defaultTab = useMemo(() => {
        if (asset?.type === 'Lifting/Slings' || asset?.addToLiftingRegister) return 'lifting';
        if (asset?.addToElectricalRegister) return 'electrical';
        if (hasInspectionTemplate) return 'general';
        return '';
    }, [asset, hasInspectionTemplate]);

    const [activeTab, setActiveTab] = useState(defaultTab);

    // Update active tab when modal opens or asset changes
    React.useEffect(() => {
        if (isOpen) {
            setActiveTab(defaultTab);
        }
    }, [isOpen, defaultTab]);

    if (!asset) return null;

    const getEncodeValue = () => {
        const origin = window.location.origin;
        switch (activeTab) {
            case 'electrical':
                return `${origin}/assets/electrical-inspections/new?assetId=${asset.id}`;
            case 'lifting':
                return `${origin}/assets/lifting-inspections/new?assetId=${asset.id}`;
            default:
                return `${origin}/assets/${asset.id}/inspect`;
        }
    };

    const encodeValue = getEncodeValue();

    const handleDownload = async () => {
        if (!qrRef.current) return;
        
        try {
            setDownloading(true);
            // Allow a small delay to ensure rendering is complete
            await new Promise(r => setTimeout(r, 100));
            const canvas = await html2canvas(qrRef.current, {
                scale: 4, // High resolution for printing
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true
            });
            
            const image = canvas.toDataURL("image/png");
            
            const a = document.createElement("a");
            a.href = image;
            const typeLabel = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
            a.download = `${typeLabel}-Inspection-QR-${asset.tag || asset.id}.png`;
            a.click();
        } catch (error) {
            console.error("Error generating QR code image", error);
        } finally {
            setDownloading(false);
        }
    };

    const tabStyles = "flex-1 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-bold text-xs gap-2 py-3";

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md rounded-[32px] p-0 overflow-hidden border-gray-100 shadow-2xl">
                <DialogHeader className="pt-8 px-8 pb-4">
                    <div className="mx-auto bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                        <QrCode size={32} className="text-blue-500" />
                    </div>
                    <DialogTitle className="text-center font-bold text-xl text-gray-900">
                        Inspection QR Code
                    </DialogTitle>
                    <p className="text-center text-sm font-medium text-gray-500 mt-2">
                        Select the inspection type to generate a direct-access QR code for <strong>{asset.name}</strong>.
                    </p>
                </DialogHeader>

                {/* Conditionally show tabs only if there's more than one active option */}
                {((hasInspectionTemplate ? 1 : 0) + (asset.addToElectricalRegister ? 1 : 0) + (asset.addToLiftingRegister || asset.type === 'Lifting/Slings' ? 1 : 0)) > 1 && (
                    <div className="px-6 mb-2">
                        <Tabs value={activeTab} onValueChange={(v: any) => setActiveTab(v)} className="w-full">
                            <TabsList className="w-full bg-gray-100/80 p-1.5 rounded-2xl h-auto">
                                {hasInspectionTemplate && (
                                    <TabsTrigger value="general" className={tabStyles}>
                                        <ClipboardCheck size={14} />
                                        General
                                    </TabsTrigger>
                                )}
                                {(asset.addToElectricalRegister) && (
                                    <TabsTrigger value="electrical" className={tabStyles}>
                                        <Zap size={14} />
                                        Electrical
                                    </TabsTrigger>
                                )}
                                {(asset.addToLiftingRegister || asset.type === 'Lifting/Slings') && (
                                    <TabsTrigger value="lifting" className={tabStyles}>
                                        <Anchor size={14} />
                                        Lifting
                                    </TabsTrigger>
                                )}
                            </TabsList>
                        </Tabs>
                    </div>
                )}

                <div className="flex flex-col items-center justify-center py-10 bg-gray-50 border-y border-gray-100 mt-4">
                    <div 
                        ref={qrRef} 
                        className="bg-white p-6 rounded-[32px] shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col items-center justify-center relative overflow-hidden"
                        style={{ width: '280px', height: '280px' }}
                    >
                        {/* Decorative background element */}
                        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-32 h-32 bg-blue-50/50 rounded-full blur-3xl -z-10" />
                        
                        <div className="p-3 bg-white rounded-2xl">
                            <QRCode
                                value={encodeValue}
                                size={200}
                                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                viewBox={`0 0 256 256`}
                                fgColor="#0f172a"
                            />
                        </div>
                        
                        <div className="mt-4 flex flex-col items-center">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                                {activeTab} Inspection
                            </span>
                            <span className="text-xs font-bold text-gray-900 mt-1">
                                {asset.tag}
                            </span>
                        </div>
                    </div>
                    
                    <p className="mt-6 text-[11px] font-bold text-gray-400 px-10 text-center uppercase tracking-widest leading-relaxed">
                        Scanning this will take you to the <br />
                        <span className="text-blue-600">{activeTab} inspection</span> form
                    </p>
                </div>

                <div className="p-8 flex justify-center bg-white">
                    <Button 
                        className="rounded-2xl font-bold bg-slate-900 hover:bg-slate-800 text-white w-full h-14 shadow-xl shadow-slate-200 transition-all active:scale-95 text-base"
                        onClick={handleDownload}
                        disabled={downloading}
                    >
                        {downloading ? (
                            <>
                                <Loader2 size={20} className="mr-3 animate-spin" />
                                Generating PNG...
                            </>
                        ) : (
                            <>
                                <Download size={20} className="mr-3" />
                                Download QR Image
                            </>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default InspectionQRModal;
